import { TranslocoService } from '@jsverse/transloco';
import {
  PolicyType,
  OdrlAction,
  OdrlCategory,
  type Constraint,
  type OdrlRule,
  type Policy
} from '@htx/policies';
import type { PolicyCardConfig, PolicyConstraintRow } from '@htx/core';

// ---------- Transloco Helpers ----------

function t(transloco: TranslocoService, key: string, params?: Record<string, unknown>): string {
  return transloco.translate(key, params ?? {});
}

function fallbackT(
  transloco: TranslocoService,
  key: string,
  fallback: string,
  params?: Record<string, unknown>
): string {
  const txt = transloco.translate(key, params ?? {});
  return txt !== key ? txt : fallback;
}

// ---------- Constraint Label & Value ----------

export function constraintLabel(transloco: TranslocoService, constraint: Constraint): string {
  switch (constraint.type) {
    case PolicyType.PARTICIPANT_RESTRICTED: {
      if (constraint.bpnNumbers?.length > 0) {
        return fallbackT(transloco, 'policy.participant.bpnl', 'BPNL');
      }
      return fallbackT(
        transloco,
        'policyType_PARTICIPANT_RESTRICTED.restriction.label',
        'Participants'
      );
    }
    case PolicyType.TIME_PERIOD_RESTRICTED:
      return fallbackT(
        transloco,
        'policyType_TIME_PERIOD_RESTRICTED.restriction.label',
        'Validity (days)'
      );
    case PolicyType.MEMBERSHIP_STATIC:
      return fallbackT(transloco, 'policyType_MEMBERSHIP_STATIC.restriction.label', 'Membership');
    case PolicyType.USE_CASE_MEMBERSHIP:
      return fallbackT(transloco, 'policyType_USE_CASE_MEMBERSHIP.restriction.label', 'Use Case');
    case PolicyType.FRAMEWORK_AGREEMENT:
      return fallbackT(transloco, 'policyBuilder.type.frameworkAgreement.label', 'Framework Agreement');
    case PolicyType.END_DATE:
      return fallbackT(transloco, 'policyType_END_DATE.restriction.label', 'End date');
    case PolicyType.COMPANY_REGION_RESTRICTED:
      return fallbackT(
        transloco,
        'policyType_COMPANY_REGION_RESTRICTED.restriction.label',
        'Company region'
      );
    case PolicyType.DATACENTER_REGION_RESTRICTED:
      return fallbackT(
        transloco,
        'policyType_DATACENTER_REGION_RESTRICTED.restriction.label',
        'Datacenter region'
      );
    case PolicyType.ALWAYS_TRUE:
      return fallbackT(transloco, 'policyType_ALWAYS_TRUE.restriction.label', 'Unrestricted');
    case PolicyType.UNKNOWN:
      return fallbackT(
        transloco,
        `policy.operand.${operandKey(constraint.leftOperand)}`,
        formatLeftOperand(constraint.leftOperand)
      );
  }
}

export function constraintValue(transloco: TranslocoService, constraint: Constraint): string {
  switch (constraint.type) {
    case PolicyType.PARTICIPANT_RESTRICTED: {
      const bpn = (constraint.bpnNumbers ?? []).join(', ');
      if (bpn) return bpn;
      return fallbackT(transloco, 'policy.participant.none', '\u2014');
    }
    case PolicyType.TIME_PERIOD_RESTRICTED: {
      const n = constraint.count;
      const daysLabel = fallbackT(transloco, 'policy.timePeriod.days', 'days', { count: n });
      return `${n} ${daysLabel}`;
    }
    case PolicyType.MEMBERSHIP_STATIC:
      return fallbackT(transloco, 'policy.membership.value', 'member');
    case PolicyType.USE_CASE_MEMBERSHIP: {
      const key = 'policy.usecase.' + constraint.useCaseId;
      const translated = transloco.translate(key);
      return translated !== key ? translated : String(constraint.useCaseId);
    }
    case PolicyType.FRAMEWORK_AGREEMENT:
      return 'DataExchangeGovernance';
    case PolicyType.END_DATE:
      return constraint.endDate.toLocaleDateString();
    case PolicyType.COMPANY_REGION_RESTRICTED:
    case PolicyType.DATACENTER_REGION_RESTRICTED:
      return String(constraint.region);
    case PolicyType.ALWAYS_TRUE:
      return fallbackT(transloco, 'policyType_ALWAYS_TRUE.restriction.value', 'No restrictions');
    case PolicyType.UNKNOWN: {
      const valKey = `policy.operand.value.${operandKey(constraint.leftOperand)}.${constraint.rightOperand}`;
      return fallbackT(transloco, valKey, constraint.rightOperand);
    }
  }
}

// ---------- Icon Maps ----------

export function constraintIcon(type: PolicyType): string {
  const icons: Record<PolicyType, string> = {
    [PolicyType.ALWAYS_TRUE]: 'lock_open',
    [PolicyType.MEMBERSHIP_STATIC]: 'verified',
    [PolicyType.PARTICIPANT_RESTRICTED]: 'group',
    [PolicyType.TIME_PERIOD_RESTRICTED]: 'schedule',
    [PolicyType.USE_CASE_MEMBERSHIP]: 'hub',
    [PolicyType.FRAMEWORK_AGREEMENT]: 'handshake',
    [PolicyType.END_DATE]: 'event',
    [PolicyType.COMPANY_REGION_RESTRICTED]: 'public',
    [PolicyType.DATACENTER_REGION_RESTRICTED]: 'dns',
    [PolicyType.UNKNOWN]: 'help_outline'
  };
  return icons[type] ?? 'help_outline';
}

export function badgeIcon(type: PolicyType): string {
  const icons: Record<PolicyType, string> = {
    [PolicyType.ALWAYS_TRUE]: 'check_circle',
    [PolicyType.MEMBERSHIP_STATIC]: 'shield',
    [PolicyType.PARTICIPANT_RESTRICTED]: 'badge',
    [PolicyType.TIME_PERIOD_RESTRICTED]: 'timer',
    [PolicyType.USE_CASE_MEMBERSHIP]: 'category',
    [PolicyType.FRAMEWORK_AGREEMENT]: 'handshake',
    [PolicyType.END_DATE]: 'event_busy',
    [PolicyType.COMPANY_REGION_RESTRICTED]: 'language',
    [PolicyType.DATACENTER_REGION_RESTRICTED]: 'storage',
    [PolicyType.UNKNOWN]: 'help_outline'
  };
  return icons[type] ?? 'help_outline';
}

export function isMonoValue(constraint: Constraint): boolean {
  return (
    constraint.type === PolicyType.PARTICIPANT_RESTRICTED ||
    constraint.type === PolicyType.UNKNOWN
  );
}

/**
 * Extracts a short key from a full URI operand.
 * E.g. ".../policy/FrameworkAgreement" → "FrameworkAgreement"
 */
export function operandKey(raw: string): string {
  return raw.replace(/^.*[#/:]/, '');
}

/**
 * Formats a raw left-operand URI into a human-readable label.
 * Strips namespace prefix and splits CamelCase into separate words.
 * E.g. "https://…/FrameworkAgreement" → "Framework Agreement"
 */
export function formatLeftOperand(raw: string): string {
  const stripped = operandKey(raw);
  const spaced = stripped.replace(/([a-z])([A-Z])/g, '$1 $2');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// ---------- Auto Explanation ----------

export function autoExplanation(transloco: TranslocoService, constraint: Constraint): string {
  const key = `policy.card.autoExplanation.${constraint.type}`;
  switch (constraint.type) {
    case PolicyType.TIME_PERIOD_RESTRICTED:
      return t(transloco, key, { count: constraint.count });
    case PolicyType.USE_CASE_MEMBERSHIP: {
      const ucKey = 'policy.usecase.' + constraint.useCaseId;
      const translated = transloco.translate(ucKey);
      const name = translated !== ucKey ? translated : String(constraint.useCaseId);
      return t(transloco, key, { useCaseId: name });
    }
    case PolicyType.FRAMEWORK_AGREEMENT:
      return t(transloco, key);
    case PolicyType.END_DATE:
      return t(transloco, key, { endDate: constraint.endDate.toLocaleDateString() });
    case PolicyType.COMPANY_REGION_RESTRICTED:
    case PolicyType.DATACENTER_REGION_RESTRICTED:
      return t(transloco, key, { region: String(constraint.region) });
    case PolicyType.UNKNOWN: {
      const label = fallbackT(
        transloco,
        `policy.operand.${operandKey(constraint.leftOperand)}`,
        formatLeftOperand(constraint.leftOperand)
      );
      return fallbackT(
        transloco,
        `policy.card.autoExplanation.operand.${operandKey(constraint.leftOperand)}`,
        `${label}: ${constraint.rightOperand}`,
        { value: constraint.rightOperand, label }
      );
    }
    default:
      return t(transloco, key);
  }
}

// ---------- Multi-Constraint Title ----------

function constraintLabelsJoined(transloco: TranslocoService, constraints: Constraint[]): string {
  return constraints.map((c) => constraintLabel(transloco, c)).join(' · ');
}

// ---------- Constraint → Row ----------

export function toConstraintRow(
  transloco: TranslocoService,
  constraint: Constraint
): PolicyConstraintRow {
  return {
    label: constraintLabel(transloco, constraint),
    value: constraintValue(transloco, constraint),
    mono: isMonoValue(constraint)
  };
}

// ---------- Policy Card Builder ----------

export function buildSinglePolicyCard(
  transloco: TranslocoService,
  policy: Policy
): PolicyCardConfig {
  const constraints = policy.constraints ?? [];
  const hasPrivateName = !!policy.privateProperties?.name?.trim();
  const hasPrivateDesc = !!policy.privateProperties?.description?.trim();

  if (constraints.length === 0) {
    return {
      severity: 'open',
      icon: 'lock_open',
      title: hasPrivateName
        ? policy.privateProperties!.name
        : t(transloco, 'policy.card.autoTitle.ALWAYS_TRUE'),
      explanation: hasPrivateDesc
        ? policy.privateProperties!.description
        : t(transloco, 'policy.card.autoExplanation.ALWAYS_TRUE'),
      badgeIcon: 'check_circle',
      typeBadge: t(transloco, 'policy.card.badge.open'),
      noConstraints: true,
      constraintRows: []
    };
  }

  if (constraints.length === 1) {
    const c = constraints[0];
    return {
      severity: 'moderate',
      icon: constraintIcon(c.type),
      title: hasPrivateName
        ? policy.privateProperties!.name
        : t(transloco, `policy.card.autoTitle.${c.type}`),
      explanation: hasPrivateDesc
        ? policy.privateProperties!.description
        : autoExplanation(transloco, c),
      badgeIcon: badgeIcon(c.type),
      typeBadge: constraintLabel(transloco, c),
      constraintRows: [toConstraintRow(transloco, c)]
    };
  }

  // Multiple constraints (AND)
  const severity = constraints.length >= 3 ? ('strict' as const) : ('combined' as const);
  return {
    severity,
    icon: 'playlist_add_check',
    title: hasPrivateName
      ? policy.privateProperties!.name
      : constraintLabelsJoined(transloco, constraints),
    explanation: hasPrivateDesc
      ? policy.privateProperties!.description
      : t(transloco, 'policy.card.autoExplanation.AND'),
    badgeIcon: 'join_inner',
    typeBadge: t(transloco, 'policy.card.badge.and', { count: constraints.length }),
    constraintRows: constraints.map((c) => toConstraintRow(transloco, c))
  };
}

export function buildPolicyCards(
  transloco: TranslocoService,
  policies: Policy[]
): PolicyCardConfig[] {
  return policies.map((policy) => buildSinglePolicyCard(transloco, policy));
}

/**
 * Unified card builder: uses ODRL rules when available, falls back to the flat
 * constraints list. Ensures consistent rendering across catalog detail page
 * and policy detail page.
 */
export function buildPolicyCardsForPolicy(
  transloco: TranslocoService,
  policy: Policy
): PolicyCardConfig[] {
  const rules = policy.rules ?? [];
  if (rules.length > 0) {
    return rules.map((r) => buildRulePolicyCard(transloco, r));
  }
  return [buildSinglePolicyCard(transloco, policy)];
}

// ---------- ODRL Helpers ----------

export function odrlActionLabel(transloco: TranslocoService, action: OdrlAction): string {
  return fallbackT(transloco, `policy.odrl.action.${action}`, action);
}

/**
 * Generates a human-readable title combining ODRL category + action.
 * Examples: "Nutzung erlaubt", "Übertragung verboten", "Lesen obligatorisch"
 */
export function odrlRuleTitle(
  transloco: TranslocoService,
  category: OdrlCategory,
  action: OdrlAction
): string {
  const actionNoun = odrlActionLabel(transloco, action);
  return fallbackT(
    transloco,
    `policy.odrl.rule.${category}`,
    `${actionNoun} (${category.toLowerCase()})`,
    { action: actionNoun }
  );
}

export function odrlCategoryIcon(category: OdrlCategory): string {
  switch (category) {
    case OdrlCategory.PERMISSION:
      return 'check_circle';
    case OdrlCategory.PROHIBITION:
      return 'block';
    case OdrlCategory.OBLIGATION:
      return 'assignment_turned_in';
  }
}

export function odrlCategorySeverityColor(
  category: OdrlCategory
): 'open' | 'moderate' | 'combined' | 'strict' {
  switch (category) {
    case OdrlCategory.PERMISSION:
      return 'open';
    case OdrlCategory.OBLIGATION:
      return 'combined';
    case OdrlCategory.PROHIBITION:
      return 'strict';
  }
}

/**
 * Generates a human-readable rule explanation for the PolicyCard.
 * Uses category-specific templates like "Die Nutzung der Daten ist uneingeschränkt erlaubt."
 */
function ruleExplanation(
  transloco: TranslocoService,
  category: OdrlCategory,
  action: OdrlAction,
  hasConstraints: boolean
): string {
  const actionNoun = odrlActionLabel(transloco, action);
  const variant = hasConstraints ? 'constrained' : 'open';
  return fallbackT(
    transloco,
    `policy.odrl.ruleExplanation.${category}.${variant}`,
    `${actionNoun} (${category.toLowerCase()})`,
    { action: actionNoun }
  );
}

export function buildRulePolicyCard(
  transloco: TranslocoService,
  rule: OdrlRule
): PolicyCardConfig {
  const ruleTitle = odrlRuleTitle(transloco, rule.category, rule.action);
  const constraints = rule.constraints ?? [];

  if (constraints.length === 0) {
    return {
      severity: odrlCategorySeverityColor(rule.category),
      icon: odrlCategoryIcon(rule.category),
      title: ruleTitle,
      explanation: ruleExplanation(transloco, rule.category, rule.action, false),
      badgeIcon: 'check_circle',
      typeBadge: t(transloco, `policy.odrl.category.${rule.category}`),
      noConstraints: true,
      constraintRows: []
    };
  }

  if (constraints.length === 1) {
    const c = constraints[0];
    return {
      severity: odrlCategorySeverityColor(rule.category),
      icon: constraintIcon(c.type),
      title: t(transloco, `policy.card.autoTitle.${c.type}`),
      explanation: autoExplanation(transloco, c),
      badgeIcon: badgeIcon(c.type),
      typeBadge: constraintLabel(transloco, c),
      constraintRows: [toConstraintRow(transloco, c)]
    };
  }

  // Multiple constraints (AND)
  const severity = constraints.length >= 3 ? ('strict' as const) : odrlCategorySeverityColor(rule.category);
  return {
    severity,
    icon: 'playlist_add_check',
    title: constraintLabelsJoined(transloco, constraints),
    explanation: t(transloco, 'policy.card.autoExplanation.AND'),
    badgeIcon: 'join_inner',
    typeBadge: t(transloco, 'policy.card.badge.and', { count: constraints.length }),
    constraintRows: constraints.map((c) => toConstraintRow(transloco, c))
  };
}

/**
 * Derives a human-readable name for a policy when no private name is set.
 * Uses constraint-based auto-titles derived from rules or the flat constraint list.
 */
export function policyAutoName(transloco: TranslocoService, policy: Policy): string {
  if (policy.privateProperties?.name?.trim()) {
    return policy.privateProperties.name;
  }
  // Collect constraints: prefer rules' constraints, fall back to flat constraints
  const rules = policy.rules ?? [];
  const constraints = rules.length > 0
    ? rules.flatMap((r) => r.constraints ?? [])
    : (policy.constraints ?? []);

  if (constraints.length === 0) {
    return t(transloco, 'policy.card.autoTitle.ALWAYS_TRUE');
  }
  if (constraints.length === 1) {
    return t(transloco, `policy.card.autoTitle.${constraints[0].type}`);
  }
  return constraintLabelsJoined(transloco, constraints);
}

/**
 * Generates an auto-description for policy cards based on constraints.
 * Collects constraints from ODRL rules or the flat constraint list and produces
 * human-readable sentences.
 */
export function policyAutoDescription(transloco: TranslocoService, policy: Policy): string {
  if (policy.privateProperties?.description?.trim()) {
    return policy.privateProperties.description;
  }
  // Collect constraints: prefer rules' constraints, fall back to flat constraints
  const rules = policy.rules ?? [];
  const constraints = rules.length > 0
    ? rules.flatMap((r) => r.constraints ?? [])
    : (policy.constraints ?? []);

  if (constraints.length === 0) {
    return fallbackT(
      transloco,
      'policy.overview.autoDescription.ALWAYS_TRUE',
      'No restrictions \u2013 open access'
    );
  }
  if (constraints.length === 1) {
    return autoExplanation(transloco, constraints[0]);
  }
  return fallbackT(transloco, 'policy.overview.autoDescription.multi', `${constraints.length} conditions (AND)`, {
    count: constraints.length
  });
}
